import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  AlantaHawksAdditionalDetailsDisclaimerTC,
  createPerkTotalLabel,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import Banner from "public/images/hawks/banner.jpg";
import LogoImage from "public/images/hawks/logo.png";
import BackgroundImage from "public/images/hawks/background.jpg";

import type { TeamConfig } from "../types";

import suiteImage1 from "public/images/hawks/suiteImage1.png";
import suiteImage2 from "public/images/hawks/suiteImage2.png";
import suiteImage3 from "public/images/hawks/suiteImage3.png";
import StaticSeatMapImage from "public/images/hawks/seat-map.jpg";

const config: TeamConfig = {
  teamSchema: "atlantahawks_suites",
  browserTitle: "Atlanta Hawks Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Atlanta Hawks",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00062-hawks-sec",
    mapId: "blockmap2d",
    suiteImages: [suiteImage1.src, suiteImage2.src, suiteImage3.src],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/hawks/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage.src,
    giftCardStandalonePageImage: "",
    findGamesBannerImage: Banner.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    seatMap3dPreviewImage: StaticSeatMapImage.src,
    logoImage: LogoImage.src + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideServiceFee: true,
    hidePackageBalanceOnCheckout: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: false,
      isParkingPriceVisible: true,
      seatCountLabel: "Tickets Included",
    },

    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },

    checkoutPerkSubtotals: {
      perkTotalLabel: createPerkTotalLabel,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: true,
    },
  },

  colors: {
    primary: "#C8102E",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: <AlantaHawksAdditionalDetailsDisclaimerTC />,
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutServiceNote:
      "Your reservation is not yet complete until notification from the Atlanta Hawks confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
